import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Input,
  Text,
  Icon,
  FormControl,
  FormLabel,
  Image,
  VStack,
  Flex,
} from '@chakra-ui/react';
import { FieldError } from 'react-hook-form';
import ImageIcon from '../../../assets/svg/image_upload.svg?react';
import Asterisk from '../Asterisk';
import useCustomToast from '../Toast';

interface ImageUploadProps {
  label?: string;
  htmlFor?: string;
  error?: FieldError;
  onChange: (file: File | null) => void;
  initialImage?: string | null;
  required?: boolean;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  label,
  htmlFor,
  error,
  onChange,
  initialImage,
  required,
}) => {
  const [imagePreview, setImagePreview] = useState<string | null>(initialImage || null);
  const [hasUserSelectedFile, setHasUserSelectedFile] = useState(false);
  const initialImageRef = useRef<string | null>(initialImage || null);
  const { showToast } = useCustomToast(); // Use custom toast
  const MAX_FILE_SIZE = 512 * 1024; // 512KB
  const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/jpg'];

  useEffect(() => {
    const newInitialImage = initialImage ?? null;
    if (newInitialImage !== initialImageRef.current && !hasUserSelectedFile) {
      setImagePreview(newInitialImage);
      initialImageRef.current = newInitialImage;
    }
  }, [initialImage, hasUserSelectedFile]);

  const validateFile = (file: File): boolean => {
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      showToast('error', 'Invalid file type. Only JPG, PNG images are allowed.');
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      showToast('error', 'File too large. Maximum file size is 512KB.');
      return false;
    }

    return true;
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;

    if (file) {
      if (!validateFile(file)) {
        event.target.value = '';
        onChange(null);
        return;
      }

      setHasUserSelectedFile(true);
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result) {
          setImagePreview(reader.result as string);
        }
      };
      reader.readAsDataURL(file);
      onChange(file);
    } else {
      setHasUserSelectedFile(true);
      setImagePreview(null);
      onChange(null);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0] || null;

    if (file) {
      if (!validateFile(file)) {
        onChange(null);
        return;
      }

      setHasUserSelectedFile(true);
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result) {
          setImagePreview(reader.result as string);
        }
      };
      reader.readAsDataURL(file);
      onChange(file);
    } else {
      setHasUserSelectedFile(true);
      setImagePreview(null);
      onChange(null);
    }
  };

  // Reset user selection flag when component unmounts (e.g., modal closes)
  useEffect(() => {
    return () => {
      setHasUserSelectedFile(false);
    };
  }, []);

  return (
    <FormControl id={htmlFor} isInvalid={!!error}>
      {label && (
        <FormLabel
          color={error ? 'status.error' : 'text.secondary'}
          fontSize={'sm'}
          fontFamily={'primary'}
          fontWeight={'normal'}
          htmlFor={htmlFor}
          mb="6px"
        >
          {label} {required && <Asterisk />}
        </FormLabel>
      )}
      <Box
        border="2px dashed"
        borderColor={error ? 'status.error' : 'border.default'}
        borderRadius="md"
        p={4}
        textAlign="center"
        cursor="pointer"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        _hover={{ borderColor: 'brand.primary.DEFAULT' }}
      >
        {imagePreview ? (
          <Box position="relative">
            <Image
              src={imagePreview}
              alt="Preview"
              boxSize="100px"
              objectFit="cover"
              mx="auto"
              borderRadius="md"
            />
            <Text mt={2} fontSize="xs" color="text.secondary">
              Drag and drop or click to replace the image (Max 512KB)
            </Text>
          </Box>
        ) : (
          <VStack fontFamily={'primary'}>
            <Icon as={ImageIcon} color={'brand.secondary.DEFAULT'} boxSize={6} />
            <Flex direction="column">
              <Text
                color={'brand.primary'}
                fontSize={'xs'}
                fontWeight={'medium'}
                textDecoration={'underline'}
              >
                Click to upload
              </Text>
              <Text fontSize="xs" color="text.secondary">
                PNG, JPG up to 512KB
              </Text>
            </Flex>
          </VStack>
        )}
        <Input
          type="file"
          accept=".jpg,.jpeg,.png"
          position="absolute"
          id={htmlFor}
          top={0}
          left={0}
          width="100%"
          height="100%"
          opacity={0}
          cursor="pointer"
          onChange={handleImageChange}
        />
      </Box>
      {error && (
        <Text fontFamily={'primary'} fontSize={'xs'} color={'status.error'} mt={2}>
          {error.message}
        </Text>
      )}
    </FormControl>
  );
};

export default ImageUpload;
